<template>
  <NavBar />
  <v-container style="margin-top: 185px;">
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center">
        <v-icon class="mr-2">mdi-comment-text-multiple</v-icon>
        {{ t.comments?.title || 'Comments' }}
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Search Filters -->
        <v-row>
          <!-- Comment Type -->
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedCommentTypeId"
              :items="commentTypes"
              item-value="id"
              item-title="displayName"
              :label="t.comments?.comment_type || 'Comment Type'"
              density="compact"
              hide-details
              clearable
              @update:model-value="onCommentTypeChange"
            ></v-select>
          </v-col>

          <!-- Element Search (only if type selected) -->
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="selectedElementId"
              :items="elements"
              item-value="id"
              item-title="name"
              :label="t.comments?.element || 'Element'"
              density="compact"
              hide-details
              clearable
              :disabled="!selectedCommentTypeId"
              :placeholder="selectedCommentTypeId ? (t.comments?.search_element || 'Search element') : (t.comments?.select_type_first || 'Select type first')"
              @update:search="onElementSearch"
            ></v-autocomplete>
          </v-col>

          <!-- Comment Text Search -->
          <v-col cols="12" md="3">
            <v-text-field
              v-model="searchTerm"
              :label="t.comments?.comment_text || 'Comment'"
              prepend-inner-icon="mdi-magnify"
              clearable
              density="compact"
              hide-details
              @keyup.enter="loadComments"
            ></v-text-field>
          </v-col>

          <!-- Search Button -->
          <v-col cols="12" md="2" class="d-flex align-center ga-2">
            <v-btn color="primary" @click="loadComments" :loading="loading">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
            <v-btn color="success" @click="openAddDialog">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Results Count -->
        <div class="text-caption mb-2" v-if="!loading">
          {{ t.common?.showing || 'Showing' }} {{ comments.length }} {{ t.common?.of || 'of' }} {{ pagination.total_count }} {{ t.comments?.comments || 'comments' }}
        </div>

        <!-- Comments Table -->
        <v-data-table
          :headers="headers"
          :items="comments"
          :loading="loading"
          :items-per-page="pagination.per_page"
          :page="pagination.page"
          density="compact"
          class="elevation-1"
          item-value="id"
          @update:page="onPageChange"
        >
          <!-- Comment ID -->
          <template v-slot:[`item.id`]="{ item }">
            <span class="font-weight-bold">#{{ item.id }}</span>
            <v-icon v-if="item.comment_parent_id" size="x-small" color="grey" class="ml-1" title="Reply">mdi-reply</v-icon>
          </template>

          <!-- Comment Type -->
          <template v-slot:[`item.comment_type_name`]="{ item }">
            <v-chip size="small" :color="getTypeColor(item.comment_type_name)">
              {{ getTypeDisplayName(item.comment_type_name) }}
            </v-chip>
          </template>

          <!-- Element (IMSI/Serial, MSISDN, etc.) -->
          <template v-slot:[`item.element_display_name`]="{ item }">
            <router-link
              v-if="item.element_display_name && getElementRoute(item)"
              :to="getElementRoute(item)"
              class="element-link"
            >
              <code>{{ item.element_display_name }}</code>
            </router-link>
            <code v-else-if="item.element_display_name">{{ item.element_display_name }}</code>
            <span v-else class="text-grey">ID: {{ item.element_id }}</span>
          </template>

          <!-- Comment Text -->
          <template v-slot:[`item.comment`]="{ item }">
            <div class="comment-text">{{ truncateText(item.comment, 100) }}</div>
          </template>

          <!-- Replies Count -->
          <template v-slot:[`item.replies_count`]="{ item }">
            <v-chip v-if="item.replies_count > 0" size="x-small" color="info" @click="openThreadDialog(item)">
              {{ item.replies_count }} {{ item.replies_count === 1 ? 'reply' : 'replies' }}
            </v-chip>
            <span v-else class="text-grey">-</span>
          </template>

          <!-- Created At -->
          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <!-- Created By -->
          <template v-slot:[`item.user_name`]="{ item }">
            {{ item.user_name || '-' }}
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex ga-1">
              <v-btn
                size="small"
                color="info"
                variant="outlined"
                @click="openThreadDialog(item)"
                :title="t.comments?.view_thread || 'View Thread'"
              >
                <v-icon class="action-icon">mdi-eye</v-icon>
              </v-btn>
              <v-btn
                size="small"
                color="success"
                variant="outlined"
                @click="openReplyDialog(item)"
                :title="t.comments?.reply || 'Reply'"
              >
                <v-icon class="action-icon">mdi-reply</v-icon>
              </v-btn>
              <v-btn
                size="small"
                color="warning"
                variant="outlined"
                @click="openEditDialog(item)"
                :title="t.common?.edit || 'Edit'"
              >
                <v-icon class="action-icon">mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                size="small"
                color="error"
                variant="outlined"
                @click="confirmDelete(item)"
                :title="t.common?.delete || 'Delete'"
              >
                <v-icon class="action-icon">mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>

        <!-- Pagination -->
        <v-pagination
          v-if="pagination.total_pages > 1"
          v-model="pagination.page"
          :length="pagination.total_pages"
          :total-visible="7"
          class="mt-4"
          @update:model-value="loadComments"
        ></v-pagination>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Comment Dialog -->
    <v-dialog v-model="commentDialog" max-width="600">
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center">
          <v-icon class="mr-2">mdi-comment-text</v-icon>
          {{ editingComment ? (t.comments?.edit_comment || 'Edit Comment') : (t.comments?.add_comment || 'Add Comment') }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-form ref="commentForm" v-model="formValid">
            <!-- Comment Type -->
            <v-select
              v-model="commentForm.comment_type_id"
              :items="commentTypes"
              item-value="id"
              item-title="displayName"
              :label="t.comments?.comment_type || 'Comment Type'"
              :rules="[v => !!v || (t.validation?.required || 'Required')]"
              :disabled="!!editingComment"
              class="mb-3"
              @update:model-value="onFormCommentTypeChange"
            ></v-select>

            <!-- Element -->
            <v-autocomplete
              v-model="commentForm.element_id"
              :items="formElements"
              item-value="id"
              item-title="name"
              :label="t.comments?.element || 'Element'"
              :rules="[v => !!v || (t.validation?.required || 'Required')]"
              :disabled="!commentForm.comment_type_id || !!editingComment"
              :placeholder="commentForm.comment_type_id ? (t.comments?.search_element || 'Search element') : (t.comments?.select_type_first || 'Select type first')"
              class="mb-3"
              @update:search="onFormElementSearch"
            ></v-autocomplete>

            <!-- Comment Text -->
            <v-textarea
              v-model="commentForm.comment"
              :label="t.comments?.comment_text || 'Comment'"
              :rules="[v => !!v || (t.validation?.required || 'Required')]"
              rows="4"
              auto-grow
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="commentDialog = false">
            {{ t.common?.cancel || 'Cancel' }}
          </v-btn>
          <v-btn color="primary" variant="flat" @click="saveComment" :loading="saving" :disabled="!formValid">
            {{ t.common?.save || 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reply Dialog -->
    <v-dialog v-model="replyDialog" max-width="600">
      <v-card>
        <v-card-title class="bg-success text-white d-flex align-center">
          <v-icon class="mr-2">mdi-reply</v-icon>
          {{ t.comments?.reply || 'Reply' }}
        </v-card-title>
        <v-card-text class="pa-4">
          <!-- Show parent comment -->
          <div v-if="replyingTo" class="mb-4">
            <div class="text-caption text-grey mb-1">{{ t.comments?.replying_to || 'Replying to' }}:</div>
            <v-card variant="outlined" class="pa-3 bg-grey-lighten-4">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="font-weight-bold">{{ replyingTo.user_name || 'Unknown' }}</span>
                <span class="text-caption">{{ formatDate(replyingTo.created_at) }}</span>
              </div>
              <pre class="comment-pre">{{ replyingTo.comment }}</pre>
            </v-card>
          </div>

          <v-form ref="replyForm" v-model="replyFormValid">
            <v-textarea
              v-model="replyForm.comment"
              :label="t.comments?.your_reply || 'Your Reply'"
              :rules="[v => !!v || (t.validation?.required || 'Required')]"
              rows="4"
              auto-grow
              autofocus
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="replyDialog = false">
            {{ t.common?.cancel || 'Cancel' }}
          </v-btn>
          <v-btn color="success" variant="flat" @click="saveReply" :loading="saving" :disabled="!replyFormValid">
            {{ t.comments?.send_reply || 'Send Reply' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Thread View Dialog -->
    <v-dialog v-model="threadDialog" max-width="800">
      <v-card v-if="threadComment">
        <v-card-title class="bg-primary text-white d-flex align-center">
          <v-icon class="mr-2">mdi-comment-text-multiple</v-icon>
          {{ t.comments?.thread || 'Comment Thread' }} #{{ threadComment.id }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-progress-linear v-if="loadingThread" indeterminate color="primary" class="mb-4"></v-progress-linear>

          <!-- Thread History (parent comments) -->
          <div v-if="threadData?.thread_history?.length > 0" class="mb-4">
            <div class="text-subtitle-2 mb-2">
              <v-icon size="small" class="mr-1">mdi-history</v-icon>
              {{ t.comments?.previous_messages || 'Previous Messages' }}
            </div>
            <div v-for="(parent, index) in threadData.thread_history" :key="parent.id" class="mb-2">
              <v-card variant="outlined" class="pa-3" :class="index < threadData.thread_history.length - 1 ? 'border-dashed' : ''">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div>
                    <span class="font-weight-bold">{{ parent.user_name || 'Unknown' }}</span>
                    <v-chip size="x-small" class="ml-2">#{{ parent.id }}</v-chip>
                  </div>
                  <span class="text-caption">{{ formatDate(parent.created_at) }}</span>
                </div>
                <pre class="comment-pre">{{ parent.comment }}</pre>
              </v-card>
              <div class="text-center py-1">
                <v-icon size="small" color="grey">mdi-arrow-down</v-icon>
              </div>
            </div>
          </div>

          <!-- Current Comment -->
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">
              <v-icon size="small" class="mr-1">mdi-comment</v-icon>
              {{ t.comments?.current_comment || 'Current Comment' }}
            </div>
            <v-card variant="outlined" class="pa-3 bg-blue-lighten-5 border-primary">
              <div class="d-flex justify-space-between align-center mb-2">
                <div>
                  <span class="font-weight-bold">{{ threadComment.user_name || 'Unknown' }}</span>
                  <v-chip size="x-small" color="primary" class="ml-2">#{{ threadComment.id }}</v-chip>
                  <v-chip size="x-small" :color="getTypeColor(threadComment.comment_type_name)" class="ml-1">
                    {{ getTypeDisplayName(threadComment.comment_type_name) }}
                  </v-chip>
                </div>
                <span class="text-caption">{{ formatDate(threadComment.created_at) }}</span>
              </div>
              <div class="text-caption text-grey mb-2">
                {{ t.comments?.element || 'Element' }}: <code>{{ threadComment.element_display_name || 'ID: ' + threadComment.element_id }}</code>
              </div>
              <pre class="comment-pre">{{ threadComment.comment }}</pre>
            </v-card>
          </div>

          <!-- Replies -->
          <div v-if="threadData?.replies?.length > 0">
            <div class="text-subtitle-2 mb-2">
              <v-icon size="small" class="mr-1">mdi-reply-all</v-icon>
              {{ t.comments?.replies || 'Replies' }} ({{ threadData.replies.length }})
            </div>
            <div v-for="reply in threadData.replies" :key="reply.id" class="mb-2">
              <v-card variant="outlined" class="pa-3 bg-green-lighten-5">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div>
                    <span class="font-weight-bold">{{ reply.user_name || 'Unknown' }}</span>
                    <v-chip size="x-small" class="ml-2">#{{ reply.id }}</v-chip>
                    <v-chip v-if="reply.replies_count > 0" size="x-small" color="info" class="ml-1" @click="openThreadDialog(reply)">
                      {{ reply.replies_count }} {{ reply.replies_count === 1 ? 'reply' : 'replies' }}
                    </v-chip>
                  </div>
                  <div class="d-flex align-center ga-2">
                    <span class="text-caption">{{ formatDate(reply.created_at) }}</span>
                    <v-btn size="x-small" color="success" variant="text" @click="openReplyDialog(reply)" :title="t.comments?.reply || 'Reply'">
                      <v-icon size="small">mdi-reply</v-icon>
                    </v-btn>
                  </div>
                </div>
                <pre class="comment-pre">{{ reply.comment }}</pre>
              </v-card>
            </div>
          </div>

          <div v-else-if="!loadingThread" class="text-center text-grey py-4">
            <v-icon size="large">mdi-comment-outline</v-icon>
            <p class="mt-2">{{ t.comments?.no_replies || 'No replies yet' }}</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn color="success" variant="outlined" @click="openReplyDialog(threadComment)">
            <v-icon class="mr-1">mdi-reply</v-icon>
            {{ t.comments?.reply || 'Reply' }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="flat" @click="threadDialog = false">
            {{ t.common?.close || 'Close' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ t.comments?.delete_comment || 'Delete Comment' }}</v-card-title>
        <v-card-text>{{ t.comments?.confirm_delete || 'Are you sure you want to delete this comment?' }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="deleteDialog = false">{{ t.common?.cancel || 'Cancel' }}</v-btn>
          <v-btn color="error" variant="flat" @click="deleteCommentConfirmed" :loading="deleting">{{ t.common?.delete || 'Delete' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getComments, getCommentDetails, getCommentTypes, searchElementsForComment, createComment, updateComment, deleteComment } from "@/composables/useCommentsAPI";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";
import NavBar from "@/components/NavBar.vue";

export default defineComponent({
  name: 'CommentsView',
  components: { NavBar },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      loadingThread: false,
      token: '',

      // Filters
      selectedCommentTypeId: null,
      selectedElementId: null,
      searchTerm: '',
      elements: [],

      // Filter options
      commentTypes: [],

      // Data
      comments: [],

      // Pagination
      pagination: {
        page: 1,
        per_page: 50,
        total_count: 0,
        total_pages: 0
      },

      // Dialogs
      commentDialog: false,
      replyDialog: false,
      threadDialog: false,
      deleteDialog: false,
      editingComment: null,
      replyingTo: null,
      threadComment: null,
      threadData: null,
      commentToDelete: null,

      // Form
      formValid: false,
      commentForm: {
        comment_type_id: null,
        element_id: null,
        comment: ''
      },
      formElements: [],

      // Reply Form
      replyFormValid: false,
      replyForm: {
        comment: ''
      },

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success'
    };
  },
  computed: {
    t() {
      return tRef.value;
    },
    headers() {
      return [
        { title: 'ID', key: 'id', sortable: true, width: '80px' },
        { title: this.t.comments?.comment_type || 'Type', key: 'comment_type_name', sortable: true, width: '120px' },
        { title: this.t.comments?.imsi_serial || 'IMSI/Serial', key: 'element_display_name', sortable: false },
        { title: this.t.comments?.comment_text || 'Comment', key: 'comment', sortable: false },
        { title: this.t.comments?.replies || 'Replies', key: 'replies_count', sortable: true, width: '100px' },
        { title: this.t.common?.created_at || 'Created At', key: 'created_at', sortable: true, width: '150px' },
        { title: this.t.common?.created_by || 'Created By', key: 'user_name', sortable: true, width: '120px' },
        { title: this.t.common?.actions || 'Actions', key: 'actions', sortable: false, width: '200px' }
      ];
    }
  },
  methods: {
    async loadComments() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          per_page: this.pagination.per_page
        };

        if (this.selectedCommentTypeId) {
          params.comment_type_id = this.selectedCommentTypeId;
        }
        if (this.selectedElementId) {
          params.element_id = this.selectedElementId;
        }
        if (this.searchTerm) {
          params.search_term = this.searchTerm;
        }

        const response = await getComments(this.token, params);

        if (response.error) {
          log("Error loading comments: " + response.message);
          this.comments = [];
        } else {
          this.comments = response.comments || [];
          this.pagination.total_count = response.total_count || 0;
          this.pagination.total_pages = response.total_pages || 0;
          this.pagination.page = response.page || 1;
        }
      } catch (err) {
        log("Exception loading comments:");
        log(err);
        this.comments = [];
      } finally {
        this.loading = false;
      }
    },

    async loadCommentTypes() {
      try {
        const response = await getCommentTypes(this.token);
        if (!response.error) {
          this.commentTypes = (response.comment_types || []).map(ct => ({
            ...ct,
            displayName: this.getTypeDisplayName(ct.name)
          }));
        }
      } catch (err) {
        log("Error loading comment types:");
        log(err);
      }
    },

    async onCommentTypeChange() {
      this.selectedElementId = null;
      this.elements = [];
      if (this.selectedCommentTypeId) {
        await this.loadElements('');
      }
    },

    async onElementSearch(searchText) {
      if (this.selectedCommentTypeId) {
        await this.loadElements(searchText || '');
      }
    },

    async loadElements(searchText) {
      try {
        const response = await searchElementsForComment(this.token, this.selectedCommentTypeId, searchText);
        if (!response.error) {
          this.elements = response.elements || [];
        }
      } catch (err) {
        log("Error loading elements:");
        log(err);
      }
    },

    async onFormCommentTypeChange() {
      this.commentForm.element_id = null;
      this.formElements = [];
      if (this.commentForm.comment_type_id) {
        await this.loadFormElements('');
      }
    },

    async onFormElementSearch(searchText) {
      if (this.commentForm.comment_type_id) {
        await this.loadFormElements(searchText || '');
      }
    },

    async loadFormElements(searchText) {
      try {
        const response = await searchElementsForComment(this.token, this.commentForm.comment_type_id, searchText);
        if (!response.error) {
          this.formElements = response.elements || [];
        }
      } catch (err) {
        log("Error loading form elements:");
        log(err);
      }
    },

    onPageChange(page) {
      this.pagination.page = page;
      this.loadComments();
    },

    openAddDialog() {
      this.editingComment = null;
      this.commentForm = {
        comment_type_id: this.selectedCommentTypeId || null,
        element_id: this.selectedElementId || null,
        comment: ''
      };
      this.formElements = [...this.elements];
      this.commentDialog = true;
    },

    openEditDialog(comment) {
      this.editingComment = comment;
      this.commentForm = {
        comment_type_id: comment.comment_type_id,
        element_id: comment.element_id,
        comment: comment.comment
      };
      this.formElements = [{ id: comment.element_id, name: comment.element_display_name || 'ID: ' + comment.element_id }];
      this.commentDialog = true;
    },

    openReplyDialog(comment) {
      this.replyingTo = comment;
      this.replyForm = { comment: '' };
      this.replyDialog = true;
      // Close thread dialog if open
      this.threadDialog = false;
    },

    async openThreadDialog(comment) {
      this.threadComment = comment;
      this.threadData = null;
      this.threadDialog = true;
      this.loadingThread = true;

      try {
        const response = await getCommentDetails(this.token, comment.id);
        if (!response.error) {
          this.threadData = response.comment;
        } else {
          log("Error loading thread: " + response.message);
        }
      } catch (err) {
        log("Exception loading thread:");
        log(err);
      } finally {
        this.loadingThread = false;
      }
    },

    confirmDelete(comment) {
      this.commentToDelete = comment;
      this.deleteDialog = true;
    },

    async saveComment() {
      if (!this.formValid) return;

      this.saving = true;
      try {
        let response;
        if (this.editingComment) {
          response = await updateComment(this.token, {
            comment_id: this.editingComment.id,
            comment: this.commentForm.comment
          });
        } else {
          response = await createComment(this.token, this.commentForm);
        }

        if (response.error) {
          this.showSnackbar(response.message, 'error');
        } else {
          this.showSnackbar(
            this.editingComment
              ? (this.t.comments?.comment_updated || 'Comment updated successfully')
              : (this.t.comments?.comment_added || 'Comment added successfully'),
            'success'
          );
          this.commentDialog = false;
          await this.loadComments();
        }
      } catch (err) {
        log("Error saving comment:");
        log(err);
        this.showSnackbar(this.t.messages?.error_occurred || 'An error occurred', 'error');
      } finally {
        this.saving = false;
      }
    },

    async saveReply() {
      if (!this.replyFormValid || !this.replyingTo) return;

      this.saving = true;
      try {
        const response = await createComment(this.token, {
          comment_type_id: this.replyingTo.comment_type_id,
          element_id: this.replyingTo.element_id,
          comment: this.replyForm.comment,
          comment_parent_id: this.replyingTo.id
        });

        if (response.error) {
          this.showSnackbar(response.message, 'error');
        } else {
          this.showSnackbar(this.t.comments?.reply_sent || 'Reply sent successfully', 'success');
          this.replyDialog = false;
          await this.loadComments();
          // Refresh thread if it was open
          if (this.threadComment) {
            await this.openThreadDialog(this.threadComment);
          }
        }
      } catch (err) {
        log("Error saving reply:");
        log(err);
        this.showSnackbar(this.t.messages?.error_occurred || 'An error occurred', 'error');
      } finally {
        this.saving = false;
      }
    },

    async deleteCommentConfirmed() {
      if (!this.commentToDelete) return;

      this.deleting = true;
      try {
        const response = await deleteComment(this.token, this.commentToDelete.id);

        if (response.error) {
          this.showSnackbar(response.message, 'error');
        } else {
          this.showSnackbar(this.t.comments?.comment_deleted || 'Comment deleted successfully', 'success');
          this.deleteDialog = false;
          await this.loadComments();
        }
      } catch (err) {
        log("Error deleting comment:");
        log(err);
        this.showSnackbar(this.t.messages?.error_occurred || 'An error occurred', 'error');
      } finally {
        this.deleting = false;
        this.commentToDelete = null;
      }
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    getTypeColor(typeName) {
      const colors = {
        'imsi': 'blue',
        'msisdn': 'green',
        'sim': 'purple',
        'client': 'orange',
        'client_service': 'teal',
        'command': 'indigo'
      };
      return colors[typeName?.toLowerCase()] || 'grey';
    },

    getTypeDisplayName(typeName) {
      const names = {
        'imsi': this.t.comments?.type_imsi || 'IMSI',
        'msisdn': this.t.comments?.type_msisdn || 'MSISDN',
        'sim': this.t.comments?.type_sim || 'SIM',
        'client': this.t.comments?.type_client || 'Client',
        'client_service': this.t.comments?.type_client_service || 'Client Service',
        'command': this.t.comments?.type_command || 'Command'
      };
      return names[typeName?.toLowerCase()] || typeName;
    },

    getElementRoute(item) {
      if (!item.element_id) return null;
      const typeName = item.comment_type_name?.toLowerCase();
      switch (typeName) {
        case 'imsi':
          return `/imsi/${item.element_id}`;
        case 'msisdn':
          return `/msisdn/${item.element_id}`;
        case 'sim':
          return `/sim/${item.element_id}`;
        case 'client':
          return `/client?client_id=${item.element_id}`;
        case 'client_service':
          return `/clientServices?cs_id=${item.element_id}`;
        case 'command':
          return `/commands?command_id=${item.element_id}`;
        default:
          return null;
      }
    },

    // Handle URL query params for deep linking
    async handleQueryParams() {
      const query = this.$route.query;
      if (query.comment_type_id) {
        this.selectedCommentTypeId = parseInt(query.comment_type_id);
        await this.loadElements('');
      }
      if (query.element_id) {
        this.selectedElementId = parseInt(query.element_id);
      }
    }
  },
  async mounted() {
    this.token = store.getters.getToken;
    await this.loadCommentTypes();
    await this.handleQueryParams();
    await this.loadComments();
  },
  watch: {
    '$route.query': {
      handler() {
        this.handleQueryParams();
        this.loadComments();
      },
      deep: true
    }
  }
});
</script>

<style scoped>
.v-table th {
  font-weight: 600 !important;
  white-space: nowrap;
}
code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
}
.element-link {
  text-decoration: none;
}
.element-link:hover code {
  background-color: #e3f2fd;
  color: #1976D2;
}
.element-link code {
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}
.comment-text {
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.comment-pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  font-family: inherit;
}
.border-dashed {
  border-style: dashed !important;
}
.border-primary {
  border-color: #1976D2 !important;
  border-width: 2px !important;
}
.action-icon {
  width: 20px !important;
  height: 20px !important;
  font-size: 20px !important;
}
</style>
